/**
 * Upload Creative page
 * Two-step flow: upload file (video or image), then add metadata
 */

import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { CreativeMetadataForm } from '@/components/creative/CreativeMetadataForm';
import { VideoUploader } from '@/components/creative/VideoUploader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useProject, useCreateCreative, useUploadUrl } from '@/hooks';
import { extractThumbnail } from '@/lib/thumbnailExtractor';
import { cn } from '@/lib/utils';
import { uploadFile, uploadBlob } from '@/services/upload';
import type { CreativeMetadata, MediaType } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

type UploadStep = 'upload' | 'metadata';

interface UploadData {
  file: File;
  duration: number;
  thumbnailUrl: string;
  mediaType: MediaType;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function UploadCreative() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [step, setStep] = React.useState<UploadStep>('upload');
  const [uploadData, setUploadData] = React.useState<UploadData | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: project, isLoading: isLoadingProject } = useProject(projectId!);
  const getUploadUrl = useUploadUrl();
  const createCreative = useCreateCreative();

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleUploadComplete = (data: UploadData) => {
    setUploadData(data);
    setError(null);
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleContinue = () => {
    if (uploadData) {
      setStep('metadata');
    }
  };

  const handleBack = () => {
    if (step === 'metadata') {
      setStep('upload');
    } else {
      navigate(`/projects/${projectId}`);
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}`);
  };

  const handleSubmit = async (formData: {
    name: string;
    metadata: CreativeMetadata;
    placements: { placement: string }[];
  }) => {
    if (!uploadData || !projectId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const isImage = uploadData.mediaType === 'image';

      // Step 1: Get upload URLs
      const uploadUrlResponse = await getUploadUrl.mutateAsync({
        projectId,
        fileName: uploadData.file.name,
        fileSize: uploadData.file.size,
        contentType: uploadData.file.type,
      });

      if (isImage) {
        // Image: upload file only (image IS the thumbnail, no separate thumbnail upload)
        await uploadFile(uploadData.file, uploadUrlResponse.uploadUrl);
      } else {
        // Video: extract thumbnail blob, upload video + thumbnail in parallel
        const thumbnailBlob = await extractThumbnail(uploadData.file);
        await Promise.all([
          uploadFile(uploadData.file, uploadUrlResponse.uploadUrl),
          uploadBlob(thumbnailBlob, uploadUrlResponse.thumbnailUploadUrl, 'image/jpeg'),
        ]);
      }

      // Step 2: Create creative record with media type
      const creative = await createCreative.mutateAsync({
        creativeId: uploadUrlResponse.creativeId,
        projectId,
        name: formData.name,
        s3Key: uploadUrlResponse.s3Key,
        // For images: thumbnailS3Key = s3Key (image is its own thumbnail)
        // For videos: use the dedicated thumbnail S3 key
        thumbnailS3Key: isImage ? uploadUrlResponse.s3Key : uploadUrlResponse.thumbnailS3Key,
        fileSize: uploadData.file.size,
        // For images: omit duration (undefined); for videos: send duration
        ...(isImage ? {} : { duration: uploadData.duration }),
        format: (() => {
          const raw = uploadData.file.type.split('/')[1] ?? (isImage ? 'jpg' : 'mp4');
          const aliases: Record<string, string> = { jpeg: 'jpg', quicktime: 'mov' };
          return aliases[raw] ?? raw;
        })(),
        mediaType: uploadData.mediaType,
        metadata: formData.metadata,
        placements: formData.placements,
      });

      // Step 3: Navigate to creative detail page
      navigate(`/creatives/${creative.creativeId}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create creative. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isLoadingProject) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent"></div>
          <p className="text-sm text-secondary">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-primary">Project not found</p>
          <Button
            variant="secondary"
            className="mt-4"
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-canvas p-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="mb-4 flex items-center gap-2 text-sm font-medium text-secondary transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            {step === 'metadata' ? 'Back to Upload' : 'Back to Project'}
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              {project.name}
            </p>
            <h1 className="mt-1 text-4xl font-black leading-tight tracking-tight text-primary">
              Upload Creative
            </h1>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            {/* Step 1: Upload */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold',
                  step === 'upload' || uploadData
                    ? 'bg-brand text-inverse'
                    : 'bg-surface-subtle text-muted'
                )}
              >
                {uploadData ? <CheckCircle2 className="h-5 w-5" /> : '1'}
              </div>
              <span
                className={cn(
                  'text-sm font-semibold',
                  step === 'upload' || uploadData
                    ? 'text-primary'
                    : 'text-muted'
                )}
              >
                Upload File
              </span>
            </div>

            {/* Connector */}
            <div
              className={cn(
                'h-0.5 flex-1',
                uploadData ? 'bg-brand' : 'bg-border-subtle'
              )}
            />

            {/* Step 2: Metadata */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold',
                  step === 'metadata'
                    ? 'bg-brand text-inverse'
                    : 'bg-surface-subtle text-muted'
                )}
              >
                2
              </div>
              <span
                className={cn(
                  'text-sm font-semibold',
                  step === 'metadata' ? 'text-primary' : 'text-muted'
                )}
              >
                Add Details
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <Card className="p-8">
          {step === 'upload' && (
            <>
              <VideoUploader
                projectId={projectId!}
                onUploadComplete={handleUploadComplete}
                onError={handleUploadError}
              />

              {/* Actions */}
              <div className="mt-8 flex gap-3">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleContinue}
                  disabled={!uploadData}
                  className="flex-1"
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 'metadata' && uploadData && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-primary">
                  Add Creative Details
                </h2>
                <p className="mt-1 text-sm text-secondary">
                  Provide metadata to help analyze and organize this creative
                </p>
              </div>

              <CreativeMetadataForm
                defaultName={uploadData.file.name.replace(/\.[^/.]+$/, '')}
                onSubmit={handleSubmit}
                isLoading={isSubmitting}
              />

              {/* Global Error */}
              {error && (
                <div className="mt-4 rounded-lg bg-danger/10 p-4 text-sm text-danger">
                  {error}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
